import { useState } from "react";
import {
  CloudOff,
  CloudUpload,
  Download,
  RefreshCw,
  Signal,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { downloadOfflineZone, listOfflineZones } from "./offline-db";
import {
  flushOfflineQueue,
  useOfflineQueue,
  useOfflineState,
} from "./offline-sync";

export function OfflineCenter() {
  const state = useOfflineState();
  const { items, retry, refresh } = useOfflineQueue();
  const [section, setSection] = useState("386");
  const [zones, setZones] = useState<
    Array<{ sectionId: number; downloadedAt: string }>
  >([]);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const loadZones = () => listOfflineZones().then(setZones);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadOfflineZone(Number(section));
      await loadZones();
      toast.success(`Zona de la sección ${section} disponible sin conexión`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No fue posible descargar la zona",
      );
    } finally {
      setDownloading(false);
    }
  };
  const count = state.pending + state.failed + state.conflicts;
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          refresh();
          loadZones();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={state.online ? "text-foreground" : "text-amber-700"}
          aria-label="Estado de conexión y sincronización"
        >
          {state.online ? <Signal /> : <WifiOff />}
          <span className="hidden md:inline">
            {state.online
              ? state.syncing
                ? "Sincronizando…"
                : "En línea"
              : "Sin conexión"}
          </span>
          {count ? (
            <Badge variant={state.conflicts ? "destructive" : "secondary"}>
              {count}
            </Badge>
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Campo sin conexión</DialogTitle>
          <DialogDescription>
            Las capturas pendientes y sus fotografías permanecen cifradas en
            este dispositivo hasta sincronizarse.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Status label="Pendientes" value={state.pending} />
            <Status label="Con error" value={state.failed} />
            <Status label="Conflictos" value={state.conflicts} />
            <Status label="Zonas" value={zones.length} />
          </div>
          <section className="rounded-lg border">
            <div className="flex items-center justify-between border-b p-3">
              <div>
                <b className="text-sm">Cola cifrada</b>
                <p className="text-xs text-muted-foreground">
                  Identificador único evita altas duplicadas.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => flushOfflineQueue()}
                disabled={!state.online || state.syncing}
              >
                <RefreshCw data-icon="inline-start" /> Sincronizar
              </Button>
            </div>
            {items.length ? (
              <div className="divide-y">
                {items.map((item) => (
                  <article
                    key={item.id}
                    className="grid gap-2 p-3 sm:grid-cols-[1fr_auto]"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <b className="text-sm">
                          Sección {item.input.territory.sectionId} ·{" "}
                          {item.input.category}
                        </b>
                        <Badge
                          variant={
                            item.status === "conflict"
                              ? "destructive"
                              : item.status === "failed"
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {item.status === "pending"
                            ? "Pendiente"
                            : item.status === "syncing"
                              ? "Sincronizando"
                              : item.status === "conflict"
                                ? "Conflicto"
                                : "Error"}
                        </Badge>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {item.input.description}
                      </p>
                      <span className="text-[11px] text-muted-foreground">
                        {item.hasPhoto ? "Con fotografía cifrada · " : ""}
                        {item.retryCount} reintentos
                      </span>
                      {item.lastError ? (
                        <p className="mt-1 text-xs text-destructive">
                          {item.lastError}
                        </p>
                      ) : null}
                    </div>
                    {item.status === "failed" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retry(item.id)}
                      >
                        <CloudUpload data-icon="inline-start" /> Reintentar
                      </Button>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <div className="grid place-items-center gap-2 p-8 text-center text-sm text-muted-foreground">
                <CloudOff />
                <span>No hay capturas pendientes.</span>
              </div>
            )}
          </section>
          <section className="rounded-lg border p-4">
            <div className="flex items-start gap-3">
              <Download className="mt-1 text-primary" />
              <div>
                <b className="text-sm">Descargar zona autorizada</b>
                <p className="text-xs text-muted-foreground">
                  Conserva cifradas las capas de sección, colonia y manzana para
                  consulta en campo.
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <div className="flex-1">
                <Label className="sr-only" htmlFor="offline-section">
                  Sección
                </Label>
                <Input
                  id="offline-section"
                  type="number"
                  min="1"
                  value={section}
                  onChange={(event) => setSection(event.target.value)}
                  placeholder="Sección"
                />
              </div>
              <Button
                onClick={download}
                disabled={
                  downloading ||
                  !state.online ||
                  !Number.isInteger(Number(section))
                }
              >
                {downloading ? "Descargando…" : "Descargar"}
              </Button>
            </div>
            {zones.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {zones.map((zone) => (
                  <Badge key={zone.sectionId} variant="outline">
                    Sección {zone.sectionId} ·{" "}
                    {new Date(zone.downloadedAt).toLocaleDateString("es-MX")}
                  </Badge>
                ))}
              </div>
            ) : null}
          </section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function Status({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-muted p-3">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <b className="text-xl">{value}</b>
    </div>
  );
}
